import { useParams, Navigate, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useSeo, SITE_URL } from "@/lib/seo";
import { getArticle, ARTICLES } from "@/data/articles";
import IMAGES from "@/data/images";
import { Breadcrumbs } from "@/components/site/PageHeader";
import { StatusBadge } from "@/components/site/StatusBadge";
import { ArticleBody } from "@/components/site/ArticleBody";
import { ArticleCard } from "@/components/site/ArticleCard";
import { ContextualCTA } from "@/components/site/ContextualCTA";

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
}

export default function Article() {
  const { slug } = useParams();
  const reduce = useReducedMotion();
  const article = getArticle(slug);
  const img = IMAGES[article?.image] || IMAGES.heroRidge;

  useSeo(
    article
      ? {
          title: article.title,
          description: article.summary,
          path: `/journal/${slug}`,
          type: "article",
          image: img.url,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.summary,
            datePublished: article.date,
            dateModified: article.updated,
            author: { "@type": "Organization", name: "Selling Sunsets" },
            publisher: { "@type": "Organization", name: "Selling Sunsets" },
            mainEntityOfPage: `${SITE_URL}/journal/${slug}`,
            image: img.url,
            articleSection: article.category,
          },
        }
      : { title: "Not found", path: `/journal/${slug}` }
  );

  if (!article) return <Navigate to="/journal" replace />;

  const related = (article.related || [])
    .map((s) => ARTICLES.find((a) => a.slug === s))
    .filter(Boolean)
    .slice(0, 3);
  const fallback = ARTICLES.filter((a) => a.slug !== slug && a.category === article.category).slice(0, 3);
  const relatedFinal = related.length ? related : fallback;

  return (
    <article>
      <header className="ss-container pt-32 lg:pt-40 pb-10">
        <Breadcrumbs items={[{ label: "Journal", to: "/journal" }, { label: article.category, to: "/journal" }, { label: article.title }]} />
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="ss-overline text-[#8C6A53]">{article.category}</span>
            <StatusBadge status={article.status} />
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] tracking-tight text-[#1A1C1E]"
          >
            {article.title}
          </motion.h1>
          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono-ss text-[0.68rem] tracking-wider uppercase text-[#8A9098]">
            <span>{article.author}</span>
            <span>Published {formatDate(article.date)}</span>
            <span>Last reviewed {formatDate(article.updated)}</span>
            <span>{article.readingTime} read</span>
          </div>
        </div>
      </header>

      <div className="ss-container pb-14">
        <motion.div
          initial={{ clipPath: reduce ? "inset(0 0 0 0)" : "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="ss-hover-grow overflow-hidden aspect-[16/8] bg-[#EFECE6]"
        >
          <img src={img.url} alt={img.alt} className="h-full w-full object-cover" />
        </motion.div>
        <p className="mt-3 font-mono-ss text-[0.64rem] tracking-wider text-[#8A9098]">{img.alt} · editorial illustration</p>
      </div>

      <div className="ss-container-text pb-16">
        <ArticleBody blocks={article.content} />

        {article.sources && article.sources.length > 0 && (
          <section className="mt-14 border-t border-[#E2DDD5] pt-8" data-testid="article-sources">
            <h2 className="font-display text-2xl font-normal text-[#1A1C1E] mb-5">Sources & further reading</h2>
            <ul className="space-y-4">
              {article.sources.map((s, i) => (
                <li key={i} className="flex gap-4">
                  <span className="font-mono-ss text-[0.7rem] text-[#C2A374] mt-1">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="text-[0.95rem] text-[#1A1C1E]">
                      <span className="font-medium">{s.org}</span> — {s.title}
                    </p>
                    {s.note && <p className="text-[0.8rem] text-[#8C6A53] mt-0.5 italic">{s.note}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <ContextualCTA
        eyebrow={article.category}
        title="Apply this to your decision"
        text="Reading is a start. For your specific situation, request an independent, buyer-side assessment."
        whatsappMessage={`Hi Suhail, I just read "${article.title}" and would like to discuss my situation.`}
      />

      {relatedFinal.length > 0 && (
        <section className="ss-container pb-28">
          <h2 className="font-display text-3xl font-light text-[#1A1C1E] mb-10">Related research</h2>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {relatedFinal.map((a, i) => <ArticleCard key={a.slug} article={a} index={i} />)}
          </div>
        </section>
      )}
    </article>
  );
}
